import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useEffect, useRef } from 'react';

import { UseCreditAgreementStore } from './store';
import { usePendingTask } from './usePendingTask';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreementFullArtifact } from '~/core/routes/settings/implementations/common/types';
import { useUpdateClientCreditAgreementFullArtifactImplementationPortalWipMutation } from '~/core/routes/settings/implementations/implementations.gql.generated';

export const useCreditAgreementServerSync = (
  useStore: UseCreditAgreementStore,
  facilityId: string,
) => {
  const toast = useToast();

  const prevCreditAgreementRef = useRef<CreditAgreementFullArtifact | null>(
    null,
  );

  const {
    creditAgreement,
    setSaveStart,
    setSaveComplete,
    setCreditAgreementDirty,
  } = useStore();
  const enqueueTask = usePendingTask();

  const [
    updateCAMutation,
  ] = useUpdateClientCreditAgreementFullArtifactImplementationPortalWipMutation();

  useEffect(() => {
    if (!creditAgreement) return () => {};

    const prev = prevCreditAgreementRef.current;
    prevCreditAgreementRef.current = creditAgreement;

    // skip on first run (prev is null) or when creditAgreement doesn't change (prev === creditAgreement)
    if (!prev || prev === creditAgreement) return () => {};

    setCreditAgreementDirty(); // A mutation will be queued, so we immediately mark the CA as dirty

    const timeoutId = window.setTimeout(() => {
      enqueueTask(async () => {
        setSaveStart();
        setCreditAgreementDirty(); // This keeps the CA dirty when there are multiple queued mutations
        try {
          await updateCAMutation({
            variables: {
              creditAgreementFullArtifactWip: {
                id: creditAgreement.id,
                creditFacilityId: facilityId,
                creditAgreement: JSON.stringify(creditAgreement),
              },
            },
          });
        } catch (e: unknown) {
          Sentry.captureException(e);
          // TODO: we need to make sure our local state accurately reflects the
          //  failure to sync. This ephemeral notice is not enough.
          toast({
            ...errorToastConfig,
            title: `Failed to sync credit agreement: ${
              e instanceof Error ? e.message : 'unknown error'
            }.`,
          });
        } finally {
          setSaveComplete();
        }
      });
    }, 3000);

    // Here we cancel any pending timeouts when the credit agreement changes.
    // This means that we won't trigger a mutation unless the credit agreement
    // is stable for 3000ms.
    return () => clearTimeout(timeoutId);
  }, [
    toast,
    creditAgreement,
    facilityId,
    updateCAMutation,
    setSaveStart,
    setSaveComplete,
    setCreditAgreementDirty,
    enqueueTask,
  ]);
};
